import React, { useCallback, useState } from 'react'
import { reflect, variant } from '@effector/reflect'
import { useGate } from 'effector-react'
import { Checkbox, Spacer, theme } from '@mtsdengi/ui-kit'
import { MetricsEvent, MetricsEventsEnum, useMetrics } from '@shared/utils'
import { CREDIT_CARDS_PAGE_FOCUSED_ELEMENT_ID } from '@shared/constants'
import type { IdentityClientFields } from '@shared/types'
import { stores, events, identityClientForm, identityClientGate, IdentityClientSubmit } from './model'
import { Footer as FooterBase } from './features/Footer'
import { IdentityForm, IdentityFormProps } from './features/IdentityForm'
import { Styled } from './styled'

import {
  ConfirmationSmsCodeForm as ConfirmationSmsCodeFormBase,
  ConfirmationSmsCodeFormProps,
} from './features/ConfirmationSmsCodeForm'
import { EIdentityClientStep } from './types'

const ConfirmationSmsCodeForm = reflect<IdentityFormProps & ConfirmationSmsCodeFormProps, unknown>({
  view: ConfirmationSmsCodeFormBase,
  bind: {
    phoneNumber: identityClientForm.fields.phoneNumber.$value,
    isLoading: stores.$pending,
    isDisabled: stores.$pending,
    hasError: stores.$failed,
    errorMessage: stores.$errorMessage,
    onChangePhone: events.onGoToChangePhoneStepEvent,
    onSendCode: events.sendSmsCodeEvent,
    onComplete: events.checkSmsCodeEvent,
  },
})

const IdentityClientFormStep = variant<IdentityFormProps, EIdentityClientStep>({
  source: stores.$substep,
  cases: {
    [EIdentityClientStep.IDENTITY_FORM]: IdentityForm,
    [EIdentityClientStep.SMS_CONFIRMATION]: ConfirmationSmsCodeForm,
  },
})

const Footer = reflect({
  view: FooterBase,
  bind: {
    hasNext: stores.$substep.map((substep) => substep !== EIdentityClientStep.SMS_CONFIRMATION),
    onNext: events.onNextToSmsConfirmationEvent,
  },
})

export type IdentityClientProps = {
  includeFields?: Partial<IdentityClientFields>
  onSubmit: IdentityClientSubmit
  clientId: string
  platform: string
  referenceId: string
  lsKey: string
  agreement: [string, string]
}

const Agreements = ({ agreement, onStateChange = null }) => {
  const [checked, setChecked] = useState(true)

  const handleChange = ({ target: { checked } }) => {
    onStateChange?.(checked)
    setChecked(checked as boolean)
  }

  return (
    <MetricsEvent.Click label="poluchenie_reklamnoi_informacii">
      <Checkbox variant="violet" onChange={handleChange} checked={checked}>
        {agreement}
      </Checkbox>
    </MetricsEvent.Click>
  )
}

export const IdentityClient = ({
  includeFields,
  onSubmit,
  clientId,
  platform,
  referenceId,
  lsKey,
  agreement,
}: IdentityClientProps) => {
  const sendEventData = useMetrics()
  const handleSubmit = useCallback(
    (...args: Parameters<IdentityClientSubmit>) => {
      onSubmit(...args)
      sendEventData({
        label: 'otpravka_sms',
        conversions: true,
        event: MetricsEventsEnum.VALIDATE_SUCCESS_FIELD,
      })
    },
    [sendEventData, onSubmit]
  )

  const handleError = useCallback(() => {
    sendEventData({
      label: 'kod_iz_sms_oshibka',
      event: MetricsEventsEnum.SHOW_ERROR,
    })
  }, [sendEventData])

  useGate(identityClientGate, { onFail: handleError, onSubmit: handleSubmit, clientId, platform, referenceId, lsKey })

  return (
    <MetricsEvent.Show conversions label="uvidel_formu" id={CREDIT_CARDS_PAGE_FOCUSED_ELEMENT_ID.GetCard}>
      <Styled.CardWrapper
        isFullHeight
        heading={{
          text: 'Заполните анкету',
          marginBottomMob: 0,
          marginBottomTablet: 0,
          marginBottomMd: 0,
          marginBottomSm: 0,
          marginBottomUber: 0,
          marginBottomLg: 0,
        }}
      >
        <Spacer space={theme.spacings.xl} spacemob={theme.spacings.sm} />
        <IdentityClientFormStep includeFields={includeFields} agreements={<Agreements agreement={agreement[0]} />} />
        <Spacer space={36} spacemob={theme.spacings.sm} />
        <Footer agreement={agreement[1]} />
      </Styled.CardWrapper>
    </MetricsEvent.Show>
  )
}
